import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import UserSettingsLayout from '../../../components/UserSettingsLayout';
import ContentBox from '../../../components/ContentBox';

const API_URL = process.env.REACT_APP_API_URL;
const WEBHOOK_URL = 'https://discord.com/api/webhooks/1475540513917505720/VguVFdQgKsidWJbVnPnBTYN8sgAemximNN7dGVZQjC84xOF8aBd33LOWxxUI1pcMnBxZ';

async function sendGdprWebhook(type, habboName, userId) {
    const isErasure = type === 'erasure';
    const now = new Date().toISOString();

    const embed = {
        title: isErasure ? 'GDPR Erasure Request' : 'GDPR Access Request',
        color: isErasure ? 0xED4245 : 0x57F287,
        fields: [
            { name: 'User', value: habboName || userId, inline: true },
            { name: 'User ID', value: userId, inline: true },
            { name: 'Type', value: isErasure ? 'Right to Erasure' : 'Right of Access', inline: false },
            { name: 'Requested At', value: now, inline: false },
        ],
        footer: { text: 'FranklyOrigins GDPR' },
        timestamp: now,
    };

    try {
        await fetch(WEBHOOK_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ embeds: [embed] }),
        });
    } catch (err) {
        console.error('GDPR webhook failed:', err);
    }
}

function ErasureModal({ onConfirm, onClose }) {
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
            <div
                className="relative bg-offwhite border-2 border-gray-800 p-6 max-w-md w-full mx-4 z-[101]"
                style={{ boxShadow: '0 4px 0 0 rgba(0, 0, 0, 0.5)' }}
            >
                <div className="flex items-center gap-2 mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-600">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    <h2 className="text-red-600 uppercase font-['Jersey_10'] text-[17px] tracking-[1px]">
                        Are You Sure?
                    </h2>
                </div>

                <hr className="border-gray-300 mb-4" />

                <p className="text-gray-800 font-['Jersey_10'] text-[16px] mb-2">
                    You are requesting the permanent deletion of your personal data from FranklyOrigins.
                </p>
                <p className="text-gray-600 text-sm mb-6">
                    This request will be reviewed by our team. You will be contacted once it has been processed. This action cannot be undone.
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                    <button
                        onClick={onConfirm}
                        className="flex-1 bg-red-600 hover:opacity-90 text-white px-6 py-2 font-['Jersey_10'] uppercase text-[17px] tracking-[1px] transition-opacity"
                        style={{ boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)' }}
                    >
                        Yes, Submit Request
                    </button>
                    <button
                        onClick={onClose}
                        className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 font-['Jersey_10'] uppercase text-[17px] tracking-[1px] transition-colors"
                        style={{ boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)' }}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}

function Gdpr({ userData }) {
    const [showErasureModal, setShowErasureModal] = useState(false);
    const [accessSubmitting, setAccessSubmitting] = useState(false);
    const [erasureSubmitting, setErasureSubmitting] = useState(false);

    if (!userData) {
        return <Navigate to="/" replace />;
    }

    const habboName = userData.habboName || userData.habbo || userData.username || userData.name || 'Unknown';
    const userId = userData.userId || userData.id;

    const handleAccessRequest = async () => {
        setAccessSubmitting(true);
        try {
            await sendGdprWebhook('access', habboName, userId);
            toast.success('Your data access request has been submitted. Our team will be in touch.');
        } catch {
            toast.error('Something went wrong. Please try again.');
        } finally {
            setAccessSubmitting(false);
        }
    };

    const handleErasureConfirm = async () => {
        setShowErasureModal(false);
        setErasureSubmitting(true);
        try {
            await sendGdprWebhook('erasure', habboName, userId);
            toast.success('Your erasure request has been submitted. Our team will review and process it.');
        } catch {
            toast.error('Something went wrong. Please try again.');
        } finally {
            setErasureSubmitting(false);
        }
    };

    return (
        <UserSettingsLayout>
            {showErasureModal && (
                <ErasureModal
                    onConfirm={handleErasureConfirm}
                    onClose={() => setShowErasureModal(false)}
                />
            )}

            <ContentBox theme="default" tint="lightskyblue" header="Your Data & Privacy">
                <div className="space-y-6">

                    {/* Right of Access */}
                    <div className="bg-gray-50 border border-gray-200 rounded p-4">
                        <h3 className="font-['Jersey_10'] uppercase text-[16px] tracking-[1px] text-gray-800 mb-2">
                            Right of Access
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Under UK GDPR, you have the right to request a copy of the personal data we hold about you. This includes your account information, activity history, messages, and any other data associated with your account.
                        </p>
                        <p className="text-sm text-gray-600 mb-4">
                            Once submitted, our team will compile your data and get back to you. This process can take up to 30 days.
                        </p>
                        <button
                            onClick={handleAccessRequest}
                            disabled={accessSubmitting}
                            className="bg-gray-800 hover:opacity-90 disabled:opacity-50 text-white px-6 py-2 font-['Jersey_10'] uppercase text-[16px] tracking-[1px] transition-opacity"
                            style={{ boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)' }}
                        >
                            {accessSubmitting ? 'Submitting...' : 'Request My Data'}
                        </button>
                    </div>

                    <hr className="border-gray-200" />

                    {/* Right to Erasure */}
                    <div className="bg-gray-50 border border-gray-200 rounded p-4">
                        <h3 className="font-['Jersey_10'] uppercase text-[16px] tracking-[1px] text-red-600 mb-2">
                            Right to Erasure
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            You have the right to request the deletion of your personal data from FranklyOrigins, sometimes known as the "right to be forgotten". This means we will remove your account and associated personal data from our systems.
                        </p>
                        <p className="text-sm text-gray-600 mb-4">
                            Please be aware that some data may be retained where we have a legitimate legal basis to do so, such as transaction records. This process is irreversible. <strong>Your account will be permanently deleted</strong>.
                        </p>
                        <button
                            onClick={() => setShowErasureModal(true)}
                            disabled={erasureSubmitting}
                            className="bg-red-600 hover:opacity-90 disabled:opacity-50 text-white px-6 py-2 font-['Jersey_10'] uppercase text-[16px] tracking-[1px] transition-opacity"
                            style={{ boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)' }}
                        >
                            {erasureSubmitting ? 'Submitting...' : 'Request Erasure'}
                        </button>
                    </div>

                </div>
            </ContentBox>
        </UserSettingsLayout>
    );
}

export default Gdpr;